/* Campaigns — the catalog, the approval moment, and the results.

   mockups/crm-v3-campaigns-v1.html sections #campaigns and #sent, drawer
   variant (Ivin, 2026-09-06: "the drawer, with Open full in its header for the
   long ones"). Same table-plus-drawer shape as Contacts and Clients.

   Three things this file exists to keep true:

   1. The number on the approval bar is resolved THIS RENDER through
      resolveSegment, never read off the record. A campaign's stored counts are
      a record of what was approved, not an input to approving it.
   2. Approving freezes the eligible ids onto the record and then hands the
      send to the callable. Nothing here talks to Mailchimp; nothing here can.
      A failure leaves the campaign `approved` with the error on the record,
      which is a state the operator can retry from.
   3. Editing the copy of an approved campaign thaws its audience and puts it
      back in review, because the person who approved a different email has not
      approved this one.

   Not in this slice: the Segments tab (slice 4) and `/crm hello` itself
   (slice 7). The check-ins row reads the interactions those will write.       */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { C, btnP, btnS, btnD, inp, mono } from "../../lib/styles.js";
import { CONSENT_STATES } from "../../lib/constants.js";
import {
  CAMPAIGN_FROM, CAMPAIGN_STATUSES, LOCAL_INPUT_RE, appendCopyVersion, audienceCounts,
  awaitingApproval, canEditCopy, campaignStatus, defaultSendAt, formatSendAt, freezeAudience,
  fromLocalInput, nextStatus, thawAudience, toLocalInput,
} from "../../lib/campaigns.js";
import { readConsent } from "../../lib/consent.js";
import { campaignApi } from "../../lib/campaignCallables.js";
import { S, cellStyle, ROW_CLASS } from "./listStyles.js";
import { CS, campaignLayout } from "./campaignStyles.js";
import { Chips, TableFooter, ListRowStyle } from "./ListParts.jsx";
import { paginate, relativeTime } from "./listModel.js";
import {
  CAMPAIGN_CHIPS, buildCampaignRows, buildCheckIns, countLine, filterCampaignRows,
  liveAudience, resolveSendAt, shortDate,
} from "./campaignModel.js";

const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const str = (v) => String(v ?? "").trim();

/* The check-ins running row is not a campaign and has no record; it is drawn
   from `interactions`. Its id is a constant so the table can key on it. */
const CHECKINS_ID = "checkins";

const EDITING_HINT = "Save or cancel the copy edit first";

/** The look of a disabled control, using nothing but opacity. */
const dim = (off) => (off ? { opacity: 0.45, cursor: "not-allowed" } : null);

/** "2 d ago", but "today" stays "today". */
const agoLabel = (rel) => (!rel ? "" : rel === "today" ? "today" : `${rel} ago`);

export default function CampaignsView({
  campaigns = [],
  segments = [],
  contacts = [],
  clients = [],
  jobs = [],
  savedQuotes = [],
  interactions = [],
  saveCampaign,
  reloadCampaigns,
  activeCampaign,
  setActiveCampaign,
  onEvidence,
  userEmail = "",
  isMobile,
  paletteOpen,
}) {
  const [chip, setChip] = useState("all");
  const [page, setPage] = useState(1);
  const [full, setFull] = useState(false);
  const rowRefs = useRef({});

  const now = useMemo(() => new Date(), []);

  const checkIns = useMemo(
    () => buildCheckIns({ interactions, contacts, now }),
    [interactions, contacts, now],
  );

  const rows = useMemo(() => buildCampaignRows({
    campaigns, segments, contacts, clients, jobs, quotes: savedQuotes, interactions, now,
  }), [campaigns, segments, contacts, clients, jobs, savedQuotes, interactions, now]);

  const visible = useMemo(() => filterCampaignRows(rows, chip), [rows, chip]);

  const [lastChip, setLastChip] = useState(chip);
  if (chip !== lastChip) { setLastChip(chip); setPage(1); }
  const pageState = paginate(visible, page);

  /* The running row shows on All and on its own chip, and is always first —
     it is the thing Ivin does most days, and it is not a campaign to scroll
     past. */
  const showRunningRow = chip === "all" || chip === "checkins";

  const openId = str(activeCampaign?.id) || (activeCampaign === CHECKINS_ID ? CHECKINS_ID : "");
  const openRow = openId && openId !== CHECKINS_ID ? rows.find((r) => r.id === openId) : null;
  const drawerOpen = Boolean(openId);

  /* Every piece of per-record UI state belongs to ONE campaign and is reset
     from the id, not from the click handlers — the same rule ContactsView
     keeps, and for the same reason: a drawer can be opened from a row, from
     the back link, or from a deep link, and a half-typed edit must not appear
     over another campaign's copy. */
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [showAudience, setShowAudience] = useState(false);
  const [sendAt, setSendAt] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [lastOpenId, setLastOpenId] = useState(openId);
  if (openId !== lastOpenId) {
    setLastOpenId(openId);
    setEditing(false);
    setDraft(null);
    setShowAudience(false);
    setBusy("");
    setError("");
    if (!openId) setFull(false);
  }

  /* ── the send time belongs to the open campaign ──────────────────────────
     Not to the block above. That block only runs when the id CHANGES, and
     there are two ordinary ways for it never to run: the view can mount with a
     campaign already open (a reload, a deep link, arriving from the Segments
     tab), and the record itself arrives asynchronously while the collection
     loads, so the first render with this id often has no schedule on it yet.

     Either way `sendAt` stayed "" — and an empty `at` handed to Approve wiped
     the campaign's own proposed time and scheduled it for nothing. Deriving it
     from a key that names both the campaign and where the value came from
     fixes both cases and still leaves a time the operator typed alone. */
  const [lastSendAtKey, setLastSendAtKey] = useState("");
  const nextSendAt = resolveSendAt({
    openId,
    recordAt: str(openRow?.record?.schedule?.at),
    lastKey: lastSendAtKey,
    fallback: () => defaultSendAt(new Date()),
  });
  if (nextSendAt) {
    setLastSendAtKey(nextSendAt.key);
    setSendAt(nextSendAt.value);
  }

  /* ── an operation belongs to ONE campaign ────────────────────────────────
     The drawer's state lives up here so it survives a re-render, which means a
     save or a send that lands after the operator has moved on would otherwise
     clear the NEXT campaign's busy flag, wipe its half-typed draft, or put
     campaign A's error over campaign B. Same rule ContactsView keeps with
     commitIfStillActive, with a sequence number as well as an id because two
     operations on the same campaign can overlap. */
  const activeIdRef = useRef(openId);
  useEffect(() => { activeIdRef.current = openId; }, [openId]);
  const opSeq = useRef(0);
  const beginOp = useCallback((id) => ({ id, token: ++opSeq.current }), []);
  const opIsCurrent = useCallback((op) => op.token === opSeq.current && activeIdRef.current === op.id, []);

  const closeDrawer = useCallback(() => {
    setActiveCampaign(null);
    setFull(false);
    if (openId) requestAnimationFrame(() => rowRefs.current[openId]?.focus?.());
  }, [openId, setActiveCampaign]);

  useEffect(() => {
    /* The ⌘K palette owns Escape while it is up (both handlers sit on the
       window). Otherwise Escape leaves full-width mode first and closes the
       drawer second, so one key never does two things at once. */
    if (!openId || paletteOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      if (full) setFull(false);
      else closeDrawer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openId, paletteOpen, full, closeDrawer]);

  const table = (
    <div>
      <ListRowStyle />
      <div style={S.box}>
        <div style={S.scroller}>
          <table style={S.table} data-testid="campaigns-table">
            <thead>
              <tr>
                <th style={S.th}>Campaign</th>
                <th style={S.th}>Audience</th>
                <th style={S.th}>Status</th>
                {/* With the drawer open the table is 420px narrower. Origin is
                    the column that goes, the way Contacts drops Tags: the
                    drawer header repeats it, and Results is the one you came
                    to the table for. */}
                {!drawerOpen && <th style={S.th}>Origin</th>}
                <th style={S.th}>When</th>
                <th style={S.th}>Results</th>
              </tr>
            </thead>
            <tbody>
              {showRunningRow && (
                <CheckInsRow
                  checkIns={checkIns}
                  selected={openId === CHECKINS_ID}
                  onOpen={() => setActiveCampaign(CHECKINS_ID)}
                  rowRefs={rowRefs}
                  drawerOpen={drawerOpen}
                />
              )}
              {pageState.rows.map((r) => {
                const selected = openId === r.id;
                const cell = (base, first) => cellStyle(base, selected, first);
                return (
                  <tr
                    key={r.id}
                    ref={(el) => { if (el) rowRefs.current[r.id] = el; else delete rowRefs.current[r.id]; }}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selected}
                    className={ROW_CLASS}
                    data-testid={`campaign-row-${r.id}`}
                    style={{ cursor: "pointer", height: 44 }}
                    onClick={() => setActiveCampaign(r.record)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setActiveCampaign(r.record); }
                    }}
                  >
                    <td style={cell(null, true)}>
                      <div style={S.who}>
                        <div style={S.whoName}>{r.name}</div>
                        <div style={S.whoMeta}>{r.meta}</div>
                      </div>
                    </td>
                    <td style={cell()}>
                      {r.segmentName || r.audience.label ? (
                        <div style={S.who}>
                          <div style={{ ...S.whoName, fontWeight: 300 }}>{r.segmentName || "—"}</div>
                          <div style={S.whoMeta}>{r.audience.label}</div>
                        </div>
                      ) : <span style={CS.resNone}>—</span>}
                    </td>
                    <td style={cell()}>
                      <span style={CS.pill(C[r.tone] || C.muted)} data-testid={`campaign-status-${r.id}`}>
                        {r.statusLabel}
                      </span>
                    </td>
                    {!drawerOpen && (
                      <td style={cell()}>
                        <span style={r.origin.chat ? CS.originChat : CS.origin}>{r.origin.label}</span>
                      </td>
                    )}
                    <td style={cell(S.mono)}>{r.when}</td>
                    <td style={cell()}><ResultsCell results={r.results} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pageState.total === 0 && (
          <div style={S.empty}>
            {rows.length === 0
              ? "No campaigns yet. The chat writes the first draft: /crm campaign <goal>."
              : "No campaigns match this filter."}
          </div>
        )}
        <TableFooter page={pageState} noun="campaign" sortLabel="newest first" onPage={setPage} />
      </div>
    </div>
  );

  return (
    <div>
      <div style={S.filters} data-testid="campaigns-filters">
        <Chips options={CAMPAIGN_CHIPS} value={chip} onChange={setChip} testid="campaigns-chip" />
        <span style={S.count} data-testid="campaigns-count">{countLine(visible, checkIns)}</span>
      </div>

      <div style={campaignLayout(drawerOpen, isMobile, full)} data-testid="campaigns-layout">
        {/* Full-width mode hides the table; so does a phone with a drawer open. */}
        {(!drawerOpen || (!isMobile && !full)) && table}
        {openId === CHECKINS_ID && (
          <CheckInsDrawer checkIns={checkIns} onClose={closeDrawer} isMobile={isMobile} />
        )}
        {openRow && (
          <CampaignDrawer
            row={openRow}
            segments={segments}
            collections={{ contacts, clients, jobs, quotes: savedQuotes, now }}
            contacts={contacts}
            saveCampaign={saveCampaign}
            reloadCampaigns={reloadCampaigns}
            beginOp={beginOp}
            opIsCurrent={opIsCurrent}
            onEvidence={onEvidence}
            userEmail={userEmail}
            onClose={closeDrawer}
            isMobile={isMobile}
            full={full}
            setFull={setFull}
            editing={editing} setEditing={setEditing}
            draft={draft} setDraft={setDraft}
            showAudience={showAudience} setShowAudience={setShowAudience}
            sendAt={sendAt} setSendAt={setSendAt}
            busy={busy} setBusy={setBusy}
            error={error} setError={setError}
          />
        )}
      </div>
    </div>
  );
}

/* ═══ the results cell ═════════════════════════════════════════════════════ */

function ResultsCell({ results }) {
  if (!results.measured) return <span style={CS.resNone}>—</span>;
  return (
    <span style={CS.res}>
      {results.openedPct}% opened
      <span style={CS.resBar}><i style={{ ...CS.resFill, width: `${results.bar}%` }} /></span>
      {results.replies} {results.replies === 1 ? "reply" : "replies"}
    </span>
  );
}

/* ═══ the check-ins running row ════════════════════════════════════════════ */

function CheckInsRow({ checkIns, selected, onOpen, rowRefs, drawerOpen }) {
  const cell = (base, first) => cellStyle(base, selected, first);
  const pct = checkIns.thisMonth ? Math.round((checkIns.replies / checkIns.thisMonth) * 100) : 0;
  return (
    <tr
      ref={(el) => { if (el) rowRefs.current[CHECKINS_ID] = el; else delete rowRefs.current[CHECKINS_ID]; }}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      className={ROW_CLASS}
      data-testid="campaign-row-checkins"
      style={{ cursor: "pointer", height: 44 }}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
    >
      <td style={cell(null, true)}>
        <div style={S.who}>
          <div style={S.whoName}>Check-ins</div>
          <div style={S.whoMeta}>
            one-email outreach · running row
            {checkIns.last ? ` · last: ${checkIns.last.name}, ${agoLabel(checkIns.last.rel)}` : ""}
          </div>
        </div>
      </td>
      <td style={cell(S.mono)}>{checkIns.thisMonth} this month</td>
      <td style={cell()}><span style={CS.pill(C.teal)}>Ongoing</span></td>
      {!drawerOpen && <td style={cell()}><span style={CS.originChat}>/crm hello</span></td>}
      <td style={cell(S.mono)}>{checkIns.last ? shortDate(checkIns.last.at) : "—"}</td>
      <td style={cell()}>
        {checkIns.thisMonth ? (
          <span style={CS.res}>
            {checkIns.replies} {checkIns.replies === 1 ? "reply" : "replies"}
            <span style={CS.resBar}><i style={{ ...CS.resFill, width: `${pct}%` }} /></span>
          </span>
        ) : <span style={CS.resNone}>—</span>}
      </td>
    </tr>
  );
}

function CheckInsDrawer({ checkIns, onClose, isMobile }) {
  return (
    <div style={isMobile ? S.drawerMobile : S.drawer} data-testid="checkins-drawer">
      <div style={S.dhead}>
        <div style={S.dback}>
          <button type="button" style={S.dbackBtn} onClick={onClose} data-testid="checkins-drawer-back">← Campaigns</button>
          <span style={{ opacity: 0.8 }}>esc</span>
        </div>
        <div style={S.dname}>Check-ins</div>
        <div style={S.dco}>One-email outreach, written by /crm hello and sent from Gmail</div>
        <div style={S.dchips}><span style={CS.pill(C.teal)}>Ongoing</span></div>
      </div>
      <div style={S.dsec}>
        <div style={S.dsecTitle}><span>This month</span></div>
        <div style={S.tiles}>
          <div style={S.tile}><div style={S.tileN}>{checkIns.thisMonth}</div><div style={S.tileL}>sent</div></div>
          <div style={S.tile}><div style={S.tileN}>{checkIns.replies}</div><div style={S.tileL}>replied</div></div>
          <div style={S.tile}><div style={S.tileN}>{checkIns.total}</div><div style={S.tileL}>all time</div></div>
        </div>
      </div>
      <div style={S.dsecLast}>
        <div style={S.dsecTitle}><span>Who</span><span style={{ ...S.dsecAction, cursor: "default" }}>newest first</span></div>
        {checkIns.rows.length === 0
          ? <div style={{ fontSize: 12.5, color: C.muted }}>Nobody yet. /crm hello writes the first one.</div>
          : (
            <ul style={CS.members} data-testid="checkins-list">
              {checkIns.rows.map((h) => (
                <li key={h.id} style={CS.member}>
                  <div>
                    <div>{h.name}</div>
                    <div style={CS.memberMeta}>{h.summary || h.subject}</div>
                  </div>
                  <span style={CS.memberTag}>
                    {shortDate(h.at)}{h.replied ? " · replied" : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
      </div>
    </div>
  );
}

/* ═══ the campaign drawer ══════════════════════════════════════════════════ */

function CampaignDrawer({
  row, segments, collections, contacts, saveCampaign, reloadCampaigns, beginOp, opIsCurrent,
  onEvidence, userEmail,
  onClose, isMobile, full, setFull,
  editing, setEditing, draft, setDraft, showAudience, setShowAudience,
  sendAt, setSendAt, busy, setBusy, error, setError,
}) {
  const record = row.record;
  const status = campaignStatus(record);
  const frozen = Boolean(str(record.audience?.frozenAt));

  /* The live resolve. For a frozen audience the "resolve" is the stored list
     read back as contacts, because a frozen campaign's audience is the record,
     not the query. */
  const resolved = useMemo(() => {
    if (frozen) {
      const ids = new Set(asArray(record.audience?.contactIds).map(str));
      const members = contacts.filter((c) => ids.has(str(c.id)));
      return { members, eligible: members, excluded: [] };
    }
    return liveAudience(record, segments, collections);
  }, [frozen, record, segments, collections, contacts]);

  const counts = useMemo(() => audienceCounts(resolved || { members: [], eligible: [] }), [resolved]);
  const exclusions = asArray(record.brief?.exclusions);
  const heldBack = exclusions.reduce((n, e) => n + (Number(e?.count) || 0), 0);
  const heldBackReason = exclusions.filter((e) => Number(e?.count) > 0).map((e) => str(e.rule)).join(", ");

  /* Every async handler runs through this: the operation is stamped with the
     campaign it belongs to, and nothing it does on the way out touches the
     screen if the operator has moved on. */
  const commit = async (next, label, existing = null) => {
    const op = existing || beginOp(record.id);
    setError("");
    setBusy(label);
    try {
      await saveCampaign({ ...next, updatedAt: new Date().toISOString() });
      return true;
    } catch (e) {
      if (opIsCurrent(op)) setError(`Could not save the campaign: ${e.message}`);
      return false;
    } finally {
      if (opIsCurrent(op)) setBusy("");
    }
  };

  /* ── opening a draft is itself a state change ──
     The plan's first transition: a draft somebody has read is awaiting a
     decision, not still being written. It only fires once there is something
     to approve — promoting an empty draft would put "Awaiting approval" on a
     campaign with no email in it. */
  useEffect(() => {
    if (status !== "draft") return;
    if (!str(record.copy?.subject) && !str(record.copy?.body)) return;
    const to = nextStatus(record, "open");
    if (!to) return;
    saveCampaign({ ...record, status: to, updatedAt: new Date().toISOString() })
      .catch((e) => console.error("campaign: could not move the draft to review", e));
  }, [status, record, saveCampaign]);

  /* ── the approval path ────────────────────────────────────────────────────
     Freeze, save, then hand the send to the callable. The two writes are
     deliberately separate: if the callable fails the campaign is `approved`
     with the frozen list and the error on the record, which is a state the
     operator can retry from without re-approving a different audience. */
  /** Pull the campaigns back from Firestore and hand back this one. */
  const refresh = useCallback(async () => {
    try {
      const fresh = await reloadCampaigns?.();
      return Array.isArray(fresh) ? fresh.find((c) => str(c.id) === str(record.id)) || null : null;
    } catch (e) {
      console.error("campaign: could not reload after the call", e);
      return null;
    }
  }, [reloadCampaigns, record.id]);

  /**
   * A callable that rejected has NOT necessarily failed.
   *
   * The commonest rejection is a timeout on the RESPONSE, and by then the
   * server may already have handed the campaign to Mailchimp. The old code
   * wrote its own pre-call snapshot back on top — status `approved`, the
   * Mailchimp id blanked — which meant the next Approve looked like a first
   * attempt and could send to the whole audience a second time.
   *
   * So nothing here writes a status or a send identity. It reloads, looks at
   * what the server actually left, and at most adds an error note on top.
   */
  const recordSendFailure = async (e, sendNow, op) => {
    const latest = await refresh();
    const say = (text) => { if (!op || opIsCurrent(op)) setError(text); };
    const landed = latest && ["scheduled", "sent", "measured"].includes(campaignStatus(latest));
    if (landed) {
      say(
        `The Suite did not get an answer (${str(e.message)}), but the server did hand it over: `
        + `this campaign is ${CAMPAIGN_STATUSES[campaignStatus(latest)].toLowerCase()}. Nothing was sent twice.`,
      );
      return;
    }
    say(`Mailchimp refused the ${sendNow ? "send" : "schedule"}: ${str(e.message)}. The audience is frozen and the campaign is approved; fix the problem and try again.`);
    // No reload, no record: the message on screen is the whole of what we know.
    if (!latest) return;
    /* The note only. `latest` is the server's own document, so this cannot
       move a status or blank an id even if the reload raced a write. */
    await saveCampaign({
      ...latest,
      send: { ...(latest.send || {}), error: str(e.message) },
      updatedAt: new Date().toISOString(),
    }).catch(() => {});
  };

  const approve = async ({ sendNow = false } = {}) => {
    const at = sendNow ? new Date().toISOString() : sendAt;
    const n = resolved?.eligible?.length || 0;
    if (!n) {
      setError("Nobody is eligible right now, so there is nothing to approve.");
      return;
    }
    /* Belt and braces behind the derivation above: an empty `at` used to be
       written straight onto the record, wiping the campaign's own proposed
       time. There is no reading of "" that means a send time. */
    if (!at) {
      setError("Pick a send time first.");
      return;
    }
    if (editing) {
      // Cannot happen through the buttons (they are disabled), and is worth
      // refusing anyway: approving would hand Mailchimp the SAVED copy while
      // the operator is looking at an edited one.
      setError("Save or cancel the copy edit first.");
      return;
    }
    if (sendNow && !window.confirm(`Send "${record.name}" to ${n} ${n === 1 ? "person" : "people"} now?`)) return;

    const to = nextStatus(record, "approve") || (status === "approved" ? "approved" : null);
    if (!to) {
      setError(`A campaign that is ${CAMPAIGN_STATUSES[status].toLowerCase()} cannot be approved.`);
      return;
    }
    const frozenRecord = {
      ...freezeAudience(record, { ...resolved, heldBack }),
      status: to,
      schedule: { ...(record.schedule || {}), at, timezone: "America/New_York" },
      send: { ...(record.send || {}), error: "" },
    };
    if (!(await commit(frozenRecord, "approving"))) return;

    const op = beginOp(record.id);
    setBusy(sendNow ? "sending" : "scheduling");
    try {
      await campaignApi.schedule({ campaignId: record.id });
      /* From here the SERVER owns the status, the schedule time and every
         send.* field — it decides between a schedule and an immediate send, it
         re-checks consent, and it writes the Mailchimp and segment ids and the
         recipient list. Reading its record back is the only way to show what
         actually happened; writing our own version of it would drop the fields
         we do not know about. */
      await refresh();
    } catch (e) {
      await recordSendFailure(e, sendNow, op);
    } finally {
      if (opIsCurrent(op)) setBusy("");
    }
  };

  const sendTest = async () => {
    const op = beginOp(record.id);
    setError("");
    setBusy("testing");
    try {
      const out = await campaignApi.test({ campaignId: record.id, to: userEmail || CAMPAIGN_FROM });
      /* The callable writes the Mailchimp campaign id itself; reading it back
         is how the drawer learns it, and it keeps this browser from being the
         one that decides what `send` holds. */
      await refresh();
      window.alert(`Test sent to ${str(out?.sentTo) || userEmail || CAMPAIGN_FROM}.`);
    } catch (e) {
      if (opIsCurrent(op)) setError(`Could not send the test: ${str(e.message)}`);
    } finally {
      if (opIsCurrent(op)) setBusy("");
    }
  };

  const discard = async () => {
    if (!window.confirm(`Discard "${record.name}"? The segment is left alone.`)) return;
    const to = nextStatus(record, "discard");
    if (!to) return;
    if (await commit({ ...record, status: to }, "discarding")) onClose();
  };

  const saveCopy = async () => {
    const edited = draft || {};
    let next = appendCopyVersion(record, edited, { by: userEmail || "Ivin", at: new Date().toISOString() });
    if (next !== record) {
      /* An approved campaign goes back to review and its audience thaws: the
         approval was of a different email. */
      const to = nextStatus(record, "edit_copy");
      if (to) next = { ...thawAudience(next), status: to };
    }
    /* One op for the save AND the editor teardown, so the sequence number does
       not move underneath the check. */
    const op = beginOp(record.id);
    if (await commit(next, "saving", op) && opIsCurrent(op)) { setEditing(false); setDraft(null); }
  };

  const versions = asArray(record.copy?.versions);
  const evidence = asArray(record.brief?.evidence);
  const showResults = ["sent", "measured"].includes(status);
  const busyLabel = busy ? `${busy[0].toUpperCase()}${busy.slice(1)}…` : "";

  return (
    <div style={isMobile || full ? S.drawerMobile : S.drawer} data-testid="campaign-drawer">
      <div style={S.dhead}>
        <div style={S.dback}>
          <button type="button" style={S.dbackBtn} onClick={onClose} data-testid="campaign-drawer-back">← Campaigns</button>
          <span style={{ display: "inline-flex", gap: 10, alignItems: "center" }}>
            {!isMobile && (
              <button
                type="button"
                style={{ ...S.dbackBtn, color: C.text }}
                data-testid="campaign-open-full"
                onClick={() => setFull(!full)}
              >
                {full ? "Close full" : "Open full"}
              </button>
            )}
            <span style={{ opacity: 0.8, fontFamily: mono }}>{row.id} · esc</span>
          </span>
        </div>
        <div style={S.dname}>{row.name}</div>
        <div style={S.dco}>{row.channelLabel} · from {CAMPAIGN_FROM} · reply-to the same</div>
        <div style={S.dchips}>
          <span style={CS.pill(C[row.tone] || C.muted)} data-testid="campaign-drawer-status">{row.statusLabel}</span>
          <span style={row.origin.chat ? CS.originChat : CS.origin}>{row.origin.label}</span>
        </div>
      </div>

      {awaitingApproval(record) || status === "approved" ? (
        <div style={S.dsec}>
          <div style={CS.approve} data-testid="campaign-approve">
            <div style={CS.approveWarn}>Your call · nothing has been sent</div>
            <div style={CS.approveLine} data-testid="campaign-approve-line">
              Audience resolved just now: <b>{counts.eligible} {counts.eligible === 1 ? "person" : "people"}</b>
              {" "}({counts.opted_in} opted in, {counts.relationship} relationship).
              {" "}{counts.no_bulk} no-bulk shown, never sent.
              {heldBack > 0 && <> {heldBack} held back{heldBackReason ? `: ${heldBackReason}` : ""}.</>}
            </div>
            {/* Everything that dispatches is inert while the copy is open in
                the editor. Approve hands MAILCHIMP the saved copy, not the one
                on screen, so approving mid-edit sends an email the operator is
                in the middle of rewriting — and a test of it is just as
                misleading, because it is a test of the wrong draft. */}
            <div style={CS.approveRow}>
              <button type="button" style={{ ...btnS, padding: "4px 9px", fontSize: 11, ...dim(busy || editing) }}
                data-testid="campaign-test" disabled={!!busy || editing}
                title={editing ? EDITING_HINT : "Send this to yourself first"}
                onClick={sendTest}>
                Send me a test
              </button>
              {/* The mockup draws this as a pill reading "Send Tue Sep 9, 9:40
                  am ▾". A native datetime-local is the pill's control: it is
                  the same shape, it works in Safari, and it is the one field
                  here an operator has to be able to type into. The title
                  carries the New York label, because the input's own display
                  gives no timezone and the whole studio runs on one. */}
              <span style={CS.sel}>
                Send
                <input
                  type="datetime-local"
                  style={CS.selInput}
                  aria-label="Send at"
                  title={`${formatSendAt(sendAt)} New York`}
                  data-testid="campaign-schedule-at"
                  disabled={!!busy || editing}
                  value={toLocalInput(sendAt)}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const next = fromLocalInput(raw);
                    if (next) { setSendAt(next); setError(""); return; }
                    /* A complete value that converts to nothing is a clock time
                       New York does not have — the March morning the clocks go
                       forward. Say so; a half-typed value says nothing. */
                    if (LOCAL_INPUT_RE.test(raw)) {
                      setError("That clock time does not exist in New York — the clocks go forward that morning. Pick a time before 2 am or after 3 am.");
                    }
                  }}
                />
              </span>
              <button type="button" style={{ ...btnP, padding: "4px 9px", fontSize: 11, ...dim(busy || editing) }}
                data-testid="campaign-approve-btn" disabled={!!busy || editing}
                title={editing ? EDITING_HINT : undefined}
                onClick={() => approve()}>
                Approve and schedule
              </button>
              {/* Not in the mockup: the plan asks for a "send now" with one
                  extra confirm naming the count. It is deliberately the
                  quietest control in the bar. */}
              <button type="button" style={{ ...S.dsecAction, fontSize: 11, ...dim(busy || editing) }}
                data-testid="campaign-send-now" disabled={!!busy || editing}
                title={editing ? EDITING_HINT : undefined}
                onClick={() => approve({ sendNow: true })}>
                or send now
              </button>
              <button type="button" style={{ ...btnD, padding: "4px 9px", fontSize: 11, marginLeft: "auto" }}
                data-testid="campaign-discard" disabled={!!busy} onClick={discard}>
                Discard
              </button>
            </div>
            {busy && <div style={CS.approveBusy} data-testid="campaign-busy">{busyLabel}</div>}
            {error && <div style={CS.approveErr} data-testid="campaign-error">{error}</div>}
            {!error && str(record.send?.error) && (
              <div style={CS.approveErr} data-testid="campaign-send-error">
                Last attempt failed: {str(record.send.error)}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {showResults && (
        <ResultsSection row={row} record={record} onEvidence={onEvidence} />
      )}

      <div style={S.dsec}>
        <div style={S.dsecTitle}>
          <span>Brief</span>
          <span style={{ ...S.dsecAction, cursor: "default" }}>
            {row.origin.chat ? "from the chat, read-only" : "read-only"}
          </span>
        </div>
        <div style={S.kv}>
          <span style={S.kvK}>Goal</span><span style={S.kvV}>{str(record.brief?.goal) || "—"}</span>
          <span style={S.kvK}>Angle</span><span style={S.kvV}>{str(record.brief?.angle) || "—"}</span>
          <span style={S.kvK}>Exclusions</span>
          <span style={S.kvV}>
            {exclusions.length
              ? exclusions.map((e) => `${str(e.rule)} (${Number(e.count) || 0})`).join(" · ")
              : "—"}
          </span>
          <span style={S.kvK}>Follow-up</span><span style={S.kvV}>{str(record.brief?.followUp) || "—"}</span>
        </div>
      </div>

      <div style={S.dsec}>
        <div style={S.dsecTitle}>
          <span>Copy</span>
          {canEditCopy(record) && !editing && (
            /* Not while a send, a schedule or a test is in flight: the copy
               that operation is carrying is the saved one, and editing under it
               makes the screen disagree with what Mailchimp was handed. */
            <button type="button" style={{ ...S.dsecAction, ...dim(busy) }} data-testid="campaign-copy-edit"
              disabled={!!busy}
              title={busy ? "Wait for the send to finish" : undefined}
              onClick={() => { setDraft({ ...(record.copy || {}) }); setEditing(true); }}>
              Edit
            </button>
          )}
        </div>
        {editing ? (
          <div data-testid="campaign-copy-form">
            <div style={CS.field}>
              <label style={CS.fieldLabel} htmlFor="cp-subject">Subject</label>
              <input id="cp-subject" style={{ ...inp, fontSize: 12, padding: "5px 8px" }}
                data-testid="campaign-copy-subject"
                value={draft?.subject ?? ""} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
            </div>
            <div style={CS.field}>
              <label style={CS.fieldLabel} htmlFor="cp-body">Body</label>
              <textarea id="cp-body" rows={9} style={{ ...inp, fontSize: 12, padding: "5px 8px", resize: "vertical" }}
                data-testid="campaign-copy-body"
                value={draft?.body ?? ""} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div style={CS.field}>
                <label style={CS.fieldLabel} htmlFor="cp-cta">Button text</label>
                <input id="cp-cta" style={{ ...inp, fontSize: 12, padding: "5px 8px" }}
                  data-testid="campaign-copy-cta"
                  value={draft?.ctaText ?? ""} onChange={(e) => setDraft({ ...draft, ctaText: e.target.value })} />
              </div>
              <div style={CS.field}>
                <label style={CS.fieldLabel} htmlFor="cp-ctaurl">Button link</label>
                <input id="cp-ctaurl" style={{ ...inp, fontSize: 12, padding: "5px 8px" }}
                  data-testid="campaign-copy-ctaurl"
                  value={draft?.ctaUrl ?? ""} onChange={(e) => setDraft({ ...draft, ctaUrl: e.target.value })} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
              <button type="button" style={btnP} data-testid="campaign-copy-save" disabled={!!busy} onClick={saveCopy}>Save</button>
              <button type="button" style={btnS} data-testid="campaign-copy-cancel"
                onClick={() => { setEditing(false); setDraft(null); }}>Cancel</button>
            </div>
            {status === "approved" && (
              <div style={{ ...CS.approveBusy, color: C.capture }}>
                Saving this thaws the frozen audience and puts the campaign back in review.
              </div>
            )}
            {error && <div style={CS.approveErr}>{error}</div>}
          </div>
        ) : (
          <>
            <div style={CS.mail} data-testid="campaign-copy">
              <div style={CS.mailSubject}>{str(record.copy?.subject) || "No subject yet"}</div>
              <div style={CS.mailBody}>{str(record.copy?.body) || "No copy yet."}</div>
              {str(record.copy?.ctaText) && str(record.copy?.ctaUrl) && (
                <span style={CS.mailCta}>{str(record.copy.ctaText)}</span>
              )}
            </div>
            <div style={CS.ver} data-testid="campaign-versions">
              <span style={{ ...CS.verTag, ...CS.verTagOn }}>
                v{versions.length + 1} · {versions.length ? "Ivin" : (row.origin.chat ? "chat" : "Ivin")}
              </span>
              {[...versions].reverse().map((v) => (
                <span key={v.n} style={CS.verTag}>v{v.n} · {v.by.split("@")[0]}, {shortDate(v.at)}</span>
              ))}
              <span>Unsubscribe line and studio address added by the template.</span>
            </div>
          </>
        )}
      </div>

      <div style={S.dsec}>
        <div style={S.dsecTitle}>
          <span>Audience</span>
          {resolved && (
            <button type="button" style={S.dsecAction} data-testid="campaign-audience-toggle"
              onClick={() => setShowAudience(!showAudience)}>
              {showAudience ? "Hide" : `See all ${resolved.members.length}`}
            </button>
          )}
        </div>
        <div style={S.kv}>
          <span style={S.kvK}>Segment</span>
          <span style={S.kvV}>
            {row.segmentName || "none"}
            {" "}<span style={S.tag}>{frozen ? "frozen" : "live"}</span>
          </span>
          <span style={S.kvK}>Freezes</span>
          <span style={S.kvV}>
            {frozen
              ? `frozen ${shortDate(record.audience.frozenAt)}, ${asArray(record.audience?.contactIds).length} ids on this record`
              : "on approval, as a list on this record"}
          </span>
          <span style={S.kvK}>Consent</span><span style={S.kvV}>applied again by the server at send</span>
        </div>
        {showAudience && resolved && (
          <ul style={CS.members} data-testid="campaign-audience-list">
            {resolved.members.map((m) => {
              const consent = readConsent(m);
              return (
                <li key={m.id} style={CS.member}>
                  <div>
                    <div>{str(m.name) || "Unnamed"}</div>
                    <div style={CS.memberMeta}>{str(m.company)}{str(m.company) && asArray(m.emails)[0] ? " · " : ""}{asArray(m.emails)[0] || ""}</div>
                  </div>
                  <span style={CS.memberTag}>
                    {consent.unsubscribed ? "unsubscribed" : (CONSENT_STATES[consent.state] || consent.state).toLowerCase()}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div style={S.dsecLast}>
        <div style={S.dsecTitle}>
          <span>Evidence</span>
          <span style={{ ...S.dsecAction, cursor: "default" }}>what the chat read</span>
        </div>
        {evidence.length === 0
          ? <div style={{ fontSize: 12.5, color: C.muted }}>None recorded.</div>
          : (
            <ul style={CS.ev} data-testid="campaign-evidence">
              {evidence.map((e, i) => (
                <li key={`${e.kind}-${e.id}-${i}`} style={{ ...CS.evItem, ...(i === evidence.length - 1 ? { borderBottom: 0 } : null) }}>
                  <i style={CS.evDot} />
                  <span>
                    {e.kind === "post" && str(e.id)
                      ? <a href={`https://newyorkcapture.com/blog/${str(e.id)}`} target="_blank" rel="noreferrer" style={CS.evLink}>{str(e.label) || str(e.id)}</a>
                      : (e.kind === "quote" || e.kind === "job")
                        ? (
                          <button type="button" style={CS.evLink}
                            onClick={() => onEvidence?.({ kind: e.kind, title: str(e.id), subtitle: str(e.label) })}>
                            {str(e.label) || `${e.kind} ${str(e.id)}`}
                          </button>
                        )
                        : <span>{str(e.label) || `${str(e.kind)} ${str(e.id)}`}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
      </div>
    </div>
  );
}

/* ═══ the sent state (mockup §#sent) ═══════════════════════════════════════ */

function ResultsSection({ row, record, onEvidence }) {
  const r = record.results || {};
  const delivered = Number(r.delivered) || 0;
  const opened = Number(r.opened) || 0;
  const replies = row.results.replyRows;
  const syncedAt = str(r.syncedAt);

  return (
    <>
      <div style={S.dsec}>
        <div style={S.dsecTitle}>
          <span>Results</span>
          <span style={{ ...S.dsecAction, cursor: "default" }}>
            {/* relativeTime says "today" for a same-day sync; "today ago" is
                not a phrase. */}
            {syncedAt ? `Mailchimp, synced ${agoLabel(relativeTime(syncedAt))}` : "not synced yet"}
          </span>
        </div>
        <div style={S.tiles} data-testid="campaign-results">
          <div style={S.tile}><div style={S.tileN}>{delivered}</div><div style={S.tileL}>delivered</div></div>
          <div style={S.tile}>
            <div style={S.tileN}>{opened}</div>
            <div style={S.tileL}>opened{delivered ? ` · ${Math.round((opened / delivered) * 100)}%` : ""}</div>
          </div>
          <div style={S.tile}><div style={S.tileN}>{replies.length}</div><div style={S.tileL}>replied</div></div>
        </div>
        <div style={{ ...S.tiles, marginTop: 8 }}>
          <div style={S.tile}><div style={S.tileN}>{Number(r.clicked) || 0}</div><div style={S.tileL}>clicked</div></div>
          <div style={S.tile}><div style={S.tileN}>{Number(r.unsubscribed) || 0}</div><div style={S.tileL}>unsubscribed</div></div>
          <div style={S.tile}><div style={S.tileN}>{Number(r.bounced) || 0}</div><div style={S.tileL}>bounced</div></div>
        </div>
      </div>

      <div style={S.dsec}>
        <div style={S.dsecTitle}>
          <span>Replies</span>
          <span style={{ ...S.dsecAction, cursor: "default" }}>from the inbox</span>
        </div>
        {replies.length === 0
          ? <div style={{ fontSize: 12.5, color: C.muted }}>Nobody has written back yet.</div>
          : (
            <ul style={CS.ev} data-testid="campaign-replies">
              {replies.slice(0, 6).map((rep, i) => (
                <li key={rep.id} style={{ ...CS.evItem, ...(i === Math.min(replies.length, 6) - 1 ? { borderBottom: 0 } : null) }}>
                  <i style={{ ...CS.evDot, ...CS.evDotLive }} />
                  <span>
                    <button type="button" style={CS.evLink}
                      onClick={() => onEvidence?.({ kind: "contact", title: rep.name, subtitle: shortDate(rep.at) })}>
                      {rep.name}
                    </button>
                    {rep.line ? ` · "${rep.line}"` : ""}
                  </span>
                </li>
              ))}
              {replies.length > 6 && (
                <li style={{ ...CS.evItem, borderBottom: 0 }}>
                  <i style={CS.evDot} /><span>{replies.length - 6} more…</span>
                </li>
              )}
            </ul>
          )}
      </div>

      <div style={S.dsec}>
        <div style={S.dsecTitle}><span>Written back</span></div>
        <div style={S.kv}>
          <span style={S.kvK}>Touches</span>
          <span style={S.kvV}>
            {asArray(record.audience?.contactIds).length} <code style={{ fontFamily: mono, fontSize: 11.5 }}>campaign_touch</code> records, one per recipient
          </span>
          <span style={S.kvK}>Consent</span>
          <span style={S.kvV}>
            {Number(r.unsubscribed) || 0} unsubscribe{(Number(r.unsubscribed) || 0) === 1 ? "" : "s"} recorded as final
          </span>
          <span style={S.kvK}>Audience</span>
          <span style={{ ...S.kvV, ...S.kvVMono }}>
            frozen list, {asArray(record.audience?.contactIds).length} ids, on this record
          </span>
        </div>
      </div>
    </>
  );
}
